import React from "react";
import {IdcardOutlined, LaptopOutlined, MailOutlined, PoundCircleOutlined} from '@ant-design/icons';
import {IAddressDetailsPaged} from "@Models/IAddressDetails";
import {GroupContext, QueryKeys, UserContext} from "@Constants";
import {getSystemId, hasCapability} from "@Utils";
import {ILicenseDetailsPaged} from "@Models/ILicenseDetails";
import {ICreditDetailsPaged} from "@Models/ICreditDetails";
import {ITag} from "@Components/groups/GroupUtils";
import {IRoleAssignmentPaged, IRolePayload} from "@Models/IRoleDetails";
import { IDeleteAction } from "./types";
import appQueryClient from "@AppQueryClient";

export enum TagType {
    Group,
    Role,
    AddressUser,
    AddressGroup,
    CreditUser,
    CreditGroup,
    LicenseGroup,
    LicenseUser,
    Assignment,
    Status,
}

export interface ITagProperties {
    icon: JSX.Element,
    color: string,
    deleteConfirm: boolean,
    deleteMessage: string,
    deleteCapability: string,
}

export const TagConfig: Map<TagType, ITagProperties> = new Map<TagType, ITagProperties>([
    [TagType.Role, {
        icon: <IdcardOutlined />,
        color: "red",
        deleteConfirm: true,
        deleteMessage: "Remove this role?",
        deleteCapability: "Role/Remove"
    }],
    [TagType.AddressUser, {
        icon: <MailOutlined />,
        color: "green",
        deleteConfirm: true,
        deleteMessage: "Remove this address?",
        deleteCapability: "Address/Delete"
    }],
    [TagType.AddressGroup, {
        icon: <MailOutlined />,
        color: "green",
        deleteConfirm: true,
        deleteMessage: "Remove this address?",
        deleteCapability: "Address/Delete"
    }],
    [TagType.CreditUser, {
        icon: <PoundCircleOutlined />,
        color: "blue",
        deleteConfirm: true,
        deleteMessage: "Remove this credit account? This cannot be undone",
        deleteCapability: "Credit/Delete"
    }],
    [TagType.CreditGroup, {
        icon: <PoundCircleOutlined />,
        color: "blue",
        deleteConfirm: true,
        deleteMessage: "Remove this credit account? This cannot be undone",
        deleteCapability: "Credit/Delete"
    }],
    [TagType.LicenseUser, {
        icon: <LaptopOutlined />,
        color: "purple",
        deleteConfirm: true,
        deleteMessage: "Delete this license?",
        deleteCapability: "License/Delete"
    }],
    [TagType.LicenseGroup, {
        icon: <LaptopOutlined />,
        color: "purple",
        deleteConfirm: true,
        deleteMessage: "Delete this license?",
        deleteCapability: "License/Delete"
    }],
    [TagType.Status, {
        icon: null,
        color: "grey",
        deleteConfirm: true,
        deleteMessage: null,
        deleteCapability: null
    }],
]);

/**
 * Get a group address tag definition
 */
export const getAddressGroupTag = (
    groupId: string,
    address: IAddressDetailsPaged,
    enableDelete: boolean,
    deleteAction: IDeleteAction
): ITag => {
    return {
        type: TagType.AddressGroup,
        groupId: groupId,
        page: address.page,
        userId: address.ownerId,
        entityId: address.addressId,
        instanceId: address.instanceId,
        context: GroupContext,
        label: "Address",
        enableDelete: enableDelete && hasCapability(
            "Address/Delete",
            GroupContext,
            address.instanceId
        ),
        deleteAction: deleteAction
    };
};

/**
 * Get a user address tag definition
 */
export const getAddressUserTag = (
    groupId: string,
    address: IAddressDetailsPaged,
    enableDelete: boolean,
    deleteAction: IDeleteAction
): ITag => {
    return {
        type: TagType.AddressUser,
        groupId: groupId,
        page: address.page,
        userId: address.instanceId,
        entityId: address.addressId,
        instanceId: address.instanceId,
        context: UserContext,
        label: "Address",
        enableDelete: enableDelete && hasCapability(
            "Address/Delete",
            UserContext,
            address.instanceId,
        ),
        deleteAction: deleteAction
    };
};

/**
 * Get a group license tag definition
 */
export const getLicenseGroupTag = (
    groupId: string,
    license: ILicenseDetailsPaged,
    deleteAction: IDeleteAction
): ITag => {
    return {
        type: TagType.LicenseGroup,
        groupId: groupId,
        page: license.page,
        userId: "",
        entityId: license.licenseId,
        instanceId: license.instanceId,
        context: GroupContext,
        label: "License",
        enableDelete: false, // License tag is only removed when all licenses are deleted in inspector.
        deleteAction: deleteAction,
    };
};

/**
 * Get a user license tag definition
 */
export const getLicenseUserTag = (
    groupId: string,
    license: ILicenseDetailsPaged,
    deleteAction: IDeleteAction
): ITag => {
    return {
        type: TagType.LicenseUser,
        groupId: groupId,
        page: license.page,
        userId: license.instanceId,
        entityId: license.licenseId,
        instanceId: license.instanceId,
        context: UserContext,
        label: "License",
        enableDelete: false, // License tag is only removed when all licenses are deleted in inspector.
        deleteAction: deleteAction,
    };
};

/**
 * Get a group credit tag definition
 */
export const getCreditGroupTag = (groupId: string, credit: ICreditDetailsPaged, deleteAction: IDeleteAction): ITag => {
    return {
        type: TagType.CreditGroup,
        groupId: groupId,
        page: credit.page,
        userId: credit.ownerId,
        entityId: credit.creditId,
        instanceId: credit.instanceId,
        context: GroupContext,
        label: "Credit",
        enableDelete: hasCapability(
            "Credit/Delete",
            GroupContext,
            credit.instanceId,
        ),
        deleteAction: deleteAction
    };
};

/**
 * Get a user credit tag definition
 */
export const getCreditUserTag = (
    groupId: string,
    credit: ICreditDetailsPaged,
    deleteAction: IDeleteAction): ITag => {
    return {
        type: TagType.CreditUser,
        groupId: groupId,
        page: credit.page,
        userId: credit.instanceId,
        entityId: credit.creditId,
        instanceId: credit.instanceId,
        context: UserContext,
        label: "Credit",
        enableDelete: hasCapability(
            "Credit/Delete",
            UserContext,
            credit.instanceId,
        ),
        deleteAction,
    };
};

/**
 * Get a user role tag definition
 */
export const getRoleUserTag = (groupId: string, role: IRoleAssignmentPaged, deleteAction: IDeleteAction): ITag => {
    const allRoles = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);

    return {
        type: TagType.Role,
        groupId: groupId,
        page: role.page,
        userId: role.userId,
        entityId: role.roleId,
        instanceId: role.roleAssignmentId,
        context: role.contextId,
        label: allRoles?.roles?.find((x) => x.roleId == role.roleId)?.name,
        enableDelete: hasCapability(
            "Role/Remove",
            role.contextId,
            role.contextId === GroupContext ? groupId : getSystemId()
        ),
        deleteAction: deleteAction
    };
};
